import { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useSession, organization } from "./lib/auth.ts";
import { useStaff } from "./lib/staff.ts";
import { Layout } from "./components/Layout.tsx";
import { StaffLayout } from "./components/StaffLayout.tsx";
import { LoginPage } from "./pages/LoginPage.tsx";
import { SignUpPage } from "./pages/SignUpPage.tsx";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage.tsx";
import { ResetPasswordPage } from "./pages/ResetPasswordPage.tsx";
import { DashboardPage } from "./pages/DashboardPage.tsx";
import { DevicesPage } from "./pages/DevicesPage.tsx";
import { DeviceDetailPage } from "./pages/DeviceDetailPage.tsx";
import { TestsPage } from "./pages/TestsPage.tsx";
import { TestDetailPage } from "./pages/TestDetailPage.tsx";
import { ReleasesPage } from "./pages/ReleasesPage.tsx";
import { OrgSetupPage } from "./pages/OrgSetupPage.tsx";
import { OrganisationPage } from "./pages/OrganisationPage.tsx";
import { ResellerPage } from "./pages/ResellerPage.tsx";
import { IntegrationsPage } from "./pages/IntegrationsPage.tsx";
import { UptickGuidePage } from "./pages/UptickGuidePage.tsx";
import { UptickBatteryGuidePage } from "./pages/UptickBatteryGuidePage.tsx";
import { BatteryPage } from "./pages/BatteryPage.tsx";
import { InvitePage } from "./pages/InvitePage.tsx";
import { AccountPage } from "./pages/AccountPage.tsx";
import { NotFoundPage } from "./pages/NotFoundPage.tsx";
import { StaffOrgsPage } from "./pages/StaffOrgsPage.tsx";
import { StaffOrgDetailPage } from "./pages/StaffOrgDetailPage.tsx";
import { StaffAssetsPage } from "./pages/StaffAssetsPage.tsx";
import { StaffAssetDetailPage } from "./pages/StaffAssetDetailPage.tsx";
import { StaffUsersPage } from "./pages/StaffUsersPage.tsx";
import { StaffAccountsPage } from "./pages/StaffAccountsPage.tsx";
import { StaffOAuthClientsPage } from "./pages/StaffOAuthClientsPage.tsx";
import { StaffAuditLogPage } from "./pages/StaffAuditLogPage.tsx";
import { StaffBackupsPage } from "./pages/StaffBackupsPage.tsx";
import { StaffAttestCasPage } from "./pages/StaffAttestCasPage.tsx";
import { StaffBatteryMakersPage } from "./pages/StaffBatteryMakersPage.tsx";
import { TermsPage } from "./pages/TermsPage.tsx";
import { PrivacyPage } from "./pages/PrivacyPage.tsx";

function RequireSession({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();
  const loc = useLocation();
  if (isPending) return <div className="center">Loading…</div>;
  if (!session?.user) {
    // Preserve the current URL so /login and /signup can bounce the user
    // back here after authenticating — invite-accept and other deep links
    // depend on this round-trip.
    const next = loc.pathname + loc.search;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <>{children}</>;
}

function RequireActiveOrg({ children }: { children: React.ReactNode }) {
  const { data: session, isPending, refetch } = useSession();
  const { isStaff, isPending: staffPending } = useStaff();
  const loc = useLocation();
  // null = not started, "none" = no memberships, "selecting" = setActive in flight.
  // Once setActive returns and refetch reads back the new activeOrganizationId,
  // we render children naturally — no extra "ok" state needed.
  const [autoSelect, setAutoSelect] = useState<null | "none" | "selecting">(null);

  useEffect(() => {
    // Better-Auth starts new sessions with activeOrganizationId=null. If the
    // user already has a membership we want to auto-pick it instead of
    // sending them through /setup-org again.
    if (isPending || !session?.user) return;
    if (session.session.activeOrganizationId) return;
    if (autoSelect !== null) return;
    setAutoSelect("selecting");
    organization.list().then(async (res) => {
      const list = res.data ?? [];
      if (list.length === 0) {
        setAutoSelect("none");
        return;
      }
      const r = await organization.setActive({ organizationId: list[0].id });
      if (r.error) {
        setAutoSelect("none");
        return;
      }
      // disableCookieCache forces a fresh /get-session round-trip; without
      // it Better-Auth would serve the stale activeOrganizationId=null
      // session out of its short-lived cookie cache.
      await refetch({ query: { disableCookieCache: true } });
    }).catch(() => setAutoSelect("none"));
  }, [isPending, session, autoSelect, refetch]);

  if (isPending || staffPending) return <div className="center">Loading…</div>;
  if (!session?.user) {
    // Preserve the deep link — printed QR labels point straight at
    // /tests/<uuid>; without `next` a logged-out scan lands on the
    // dashboard after login instead of the test.
    const next = loc.pathname + loc.search;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  if (session.session.activeOrganizationId) return <>{children}</>;
  // Staff with no client orgs go to /staff; non-staff still hit /setup-org.
  if (autoSelect === "none") {
    return <Navigate to={isStaff ? "/staff" : "/setup-org"} replace />;
  }
  return <div className="center">Loading…</div>;
}

function RequireStaff({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();
  const { isStaff, isPending: staffPending } = useStaff();
  const loc = useLocation();
  if (isPending || staffPending) return <div className="center">Loading…</div>;
  if (!session?.user) {
    const next = loc.pathname + loc.search;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  if (!isStaff) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// Public-registration kill switch. Mirrors the server-side
// emailAndPassword.disableSignUp + socialProviders.disableImplicitSignUp
// gate in auth.ts. The frontend hide is for UX (no dead "Sign up" link
// or 404 page when a user navigates to /signup directly); the server
// gate is the actual enforcement.
//
// VITE_PUBLIC_SIGNUP is read at BUILD TIME — flipping it requires
// rebuilding + redeploying the web bundle.
export const PUBLIC_SIGNUP_ENABLED =
  import.meta.env.VITE_PUBLIC_SIGNUP === "true";

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/signup"
        element={
          PUBLIC_SIGNUP_ENABLED
            ? <SignUpPage />
            : <Navigate to="/login" replace />
        }
      />
      {/* Password-reset pages stay enabled even when public signup is
          off — invited users still need a way to recover access. */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password"  element={<ResetPasswordPage />} />
      {/* Public legal pages. Microsoft's publisher-verification crawler
          fetches these (configured in the Entra app's Branding blade)
          so they must return 200 from any origin without auth. */}
      <Route path="/terms"           element={<TermsPage />} />
      <Route path="/privacy"         element={<PrivacyPage />} />
      <Route
        path="/setup-org"
        element={<RequireSession><OrgSetupPage /></RequireSession>}
      />
      <Route
        path="/invite/:id"
        element={<RequireSession><InvitePage /></RequireSession>}
      />
      <Route
        path="/staff"
        element={<RequireStaff><StaffLayout /></RequireStaff>}
      >
        <Route index element={<StaffOrgsPage />} />
        <Route path="orgs/:orgId" element={<StaffOrgDetailPage />} />
        <Route path="assets" element={<StaffAssetsPage />} />
        <Route path="assets/:assetSerial" element={<StaffAssetDetailPage />} />
        <Route path="accounts" element={<StaffAccountsPage />} />
        <Route path="users" element={<StaffUsersPage />} />
        <Route path="oauth-clients" element={<StaffOAuthClientsPage />} />
        <Route path="audit-log" element={<StaffAuditLogPage />} />
        <Route path="backups" element={<StaffBackupsPage />} />
        <Route path="attest-cas" element={<StaffAttestCasPage />} />
        <Route path="battery-makers" element={<StaffBatteryMakersPage />} />
        <Route path="releases" element={<ReleasesPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      <Route
        path="/"
        element={<RequireActiveOrg><Layout /></RequireActiveOrg>}
      >
        <Route index element={<DashboardPage />} />
        <Route path="devices" element={<DevicesPage />} />
        <Route path="devices/:deviceId" element={<DeviceDetailPage />} />
        <Route path="tests" element={<TestsPage />} />
        <Route path="tests/:testId" element={<TestDetailPage />} />
        <Route path="batteries/:batteryId" element={<BatteryPage />} />
        <Route path="organisation" element={<OrganisationPage />} />
        <Route path="integrations" element={<IntegrationsPage />} />
        <Route path="integrations/uptick-guide" element={<UptickGuidePage />} />
        <Route path="integrations/uptick-batteries" element={<UptickBatteryGuidePage />} />
        <Route path="reseller" element={<ResellerPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
